import React, { useEffect } from 'react';
import useAuth from '#/hooks/useAuth';
import useLocalStorage from '#/hooks/useLocalStorage';
import { ROUTE_NAMES } from '#/router/routes';
import ApiManager from '#/services/api/ApiManager';
import { ITokenData, ITokenResponse } from '#/types/API/token';
import { spotifyApiConfig, spotifyTokenApiConfig } from '#/utils/constants';
import { generateCodeChallenge, generateRandomString } from '#/utils/functions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '#/components/Form/Button';
import Header from '#/components/Form/Header';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import IUserProfileResponse from '#/types/API/userProfile';
import { useDispatch } from 'react-redux';
import { createActionProfileAddProfile } from '#/redux/actions/profile';

const Login = () => {
  const [storeCodeVerifier, retrieveCodeVerifier, removeCodeVerifier] =
    useLocalStorage<string>('code_verifier');

  const protectedSpotify = useProtectedSpotify();

  const dispatch = useDispatch();

  const [searchParameters, setSearchParameters] = useSearchParams();

  const { storeAuth, retrieveAuth } = useAuth();

  const navigate = useNavigate();

  const handleLogin = async () => {
    const codeVerifier = generateRandomString(128);

    generateCodeChallenge(codeVerifier).then((codeChallenge) => {
      const state = generateRandomString(16);
      const scope =
        'streaming user-read-private user-read-email user-top-read user-modify-playback-state user-library-read user-follow-read user-follow-modify user-library-modify playlist-modify-public playlist-modify-private';

      storeCodeVerifier(codeVerifier);

      if (
        process.env.REACT_APP_SPOTIFY_CLIENT_ID &&
        process.env.REACT_APP_SPOTIFY_REDIRECT_URI_AFTER_LOGIN
      ) {
        const arguments_ = new URLSearchParams({
          response_type: 'code',
          client_id: process.env.REACT_APP_SPOTIFY_CLIENT_ID,
          scope: scope,
          redirect_uri: process.env.REACT_APP_SPOTIFY_REDIRECT_URI_AFTER_LOGIN,
          state: state,
          code_challenge_method: 'S256',
          code_challenge: codeChallenge,
        });

        const redirectTo =
          'https://accounts.spotify.com/authorize?' + arguments_;

        window.location.replace(redirectTo);
      }
    });
  };

  useEffect(() => {
    const code = searchParameters.get('code');

    if (code) {
      const codeVerifier = retrieveCodeVerifier();

      if (codeVerifier) {
        const getToken = async () => {
          const body = {
            grant_type: 'authorization_code',
            code: code,
            redirect_uri:
              process.env.REACT_APP_SPOTIFY_REDIRECT_URI_AFTER_LOGIN,
            client_id: process.env.REACT_APP_SPOTIFY_CLIENT_ID,
            code_verifier: codeVerifier,
          };

          const headers = {
            'Content-Type': 'application/x-www-form-urlencoded',
          };

          const apiManager = new ApiManager(spotifyTokenApiConfig);

          const response = await apiManager.createEntity<
            ITokenData,
            ITokenResponse
          >('token', body, headers);

          if ('data' in response) {
            storeAuth({
              token: response.data.access_token,
              refreshToken: response.data.refresh_token,
            });

            removeCodeVerifier();

            const apiManager = new ApiManager(
              spotifyApiConfig,
              protectedSpotify
            );

            const responseProfile =
              await apiManager.getAllEntities<IUserProfileResponse>('me', []);
            // eslint-disable-next-line no-console
            if ('error' in responseProfile) {
              // TODO: handleError
            } else {
              dispatch(createActionProfileAddProfile(responseProfile.data));
              navigate(ROUTE_NAMES.HOME);
            }
          } else {
            if (searchParameters.get('code')) {
              searchParameters.delete('code');
              setSearchParameters(searchParameters);
            }

            if (searchParameters.get('state')) {
              searchParameters.delete('state');
              setSearchParameters(searchParameters);
            }
            // TODO Handle error
          }
        };
        getToken();
      }
    }
  }, [
    dispatch,
    navigate,
    protectedSpotify,
    removeCodeVerifier,
    retrieveAuth,
    retrieveCodeVerifier,
    searchParameters,
    setSearchParameters,
    storeAuth,
  ]);

  return (
    <div className="flex flex-col justify-center items-center h-screen">
      <div className="flex flex-col gap-4 items-center max-w-xs md:max-w-md">
        <Header type="h1" className="font-bold">
          Clonetify
        </Header>
        <p className=" text-center">
          This app let you browse albums, artists, tracks and more utilizing the
          Spotify Web API. Following the Spotify Developer terms of service we
          need you to authorize Clonetify to read your Spotify account data
          before using it.
        </p>
        <Button onClick={handleLogin}>Authorize</Button>
      </div>
    </div>
  );
};

export default Login;
